import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Chip,
  useMediaQuery,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { TreeItem, SimpleTreeView } from "@mui/x-tree-view";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Business as ClinicIcon,
  Description as PageIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import {
  getClinicsAndPagesByProject,
  createClinic,
  updateClinic,
  deleteClinic,
} from "../../controllers/clinic.controller";
import { createPage, updatePage, deletePage } from "../../controllers/page.controller";

import ClinicForm from "./ClinicForm";
import PageForm from "./PageForm";

const stopAll = (e) => {
  // ✅ مهم: هم کلیک TreeItem را می‌گیرد، هم navigate را
  e.preventDefault();
  e.stopPropagation();
};

const nodeTitleFa = (node) => {
  if (!node) return "---";
  const t = node.title;
  return typeof t === "object" ? t?.fa ?? "---" : t ?? "---";
};

export default function HierarchyTree({ projectId }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [treeData, setTreeData] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [mode, setMode] = useState(null); // 'edit-clinic' | 'create-clinic' | 'edit-page' | 'create-page'

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    const data = await getClinicsAndPagesByProject(projectId);
    setTreeData(data || []);
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const childrenMap = useMemo(() => {
    // parentId -> children[]
    const map = new Map();
    for (const item of treeData) {
      const p = item.parentId ?? null;
      if (!map.has(p)) map.set(p, []);
      map.get(p).push(item);
    }
    return map;
  }, [treeData]);

  const openModal = (type, node = null) => {
    setMode(type);
    setSelectedNode(node);
    setOpenDialog(true);
  };

  const closeModal = () => {
    setOpenDialog(false);
    setSelectedNode(null);
    setMode(null);
  };

  const handleClinicSubmit = async (formData) => {
    if (mode === "create-clinic") {
      await createClinic({ ...formData, projectId });
    } else if (mode === "edit-clinic") {
      const id = selectedNode?.id?.replace("clinic-", "");
      await updateClinic(id, formData);
    }
    closeModal();
    fetchData();
  };

  const handlePageSubmit = async (formData) => {
    if (mode === "create-page") {
      const clinicId = selectedNode?.id?.replace("clinic-", "");
      await createPage({ ...formData, clinicId });
    } else if (mode === "edit-page") {
      const id = selectedNode?.id?.replace("page-", "");
      await updatePage(id, formData);
    }
    closeModal();
    fetchData();
  };

  const handleDelete = async (node) => {
    const id = node.id.replace(/^(clinic|page)-/, "");
    const name = nodeTitleFa(node);

    if (
      window.confirm(
        `آیا از حذف ${node.type === "Clinic" ? "کلینیک" : "صفحه"} "${name}" مطمئن هستید؟`
      )
    ) {
      if (node.type === "Clinic") await deleteClinic(id);
      else await deletePage(id);
      fetchData();
    }
  };

  const onPageRowClick = (node) => {
    // ✅ فقط وقتی روی خود ردیف page کلیک می‌شود، برو builder
    if (node.type !== "Page") return;
    const pageId = node.id.replace(/^page-/, "");
    navigate(`/forms/${pageId}/builder`);
  };

  const renderActions = (node) => {
    if (node.type === "Clinic") {
      return (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="افزودن صفحه">
            <IconButton
              size={isMobile ? "medium" : "small"}
              onClick={(e) => {
                stopAll(e);
                openModal("create-page", node);
              }}
              aria-label="add-page"
            >
              <AddIcon fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
          </Tooltip>

          <Tooltip title="ویرایش کلینیک">
            <IconButton
              size={isMobile ? "medium" : "small"}
              onClick={(e) => {
                stopAll(e);
                openModal("edit-clinic", node);
              }}
              aria-label="edit-clinic"
            >
              <EditIcon fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
          </Tooltip>

          <Tooltip title="حذف کلینیک">
            <IconButton
              size={isMobile ? "medium" : "small"}
              color="error"
              onClick={(e) => {
                stopAll(e);
                handleDelete(node);
              }}
              aria-label="delete-clinic"
            >
              <DeleteIcon fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
          </Tooltip>
        </Stack>
      );
    }

    if (node.type === "Page") {
      return (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="ویرایش صفحه">
            <IconButton
              size={isMobile ? "medium" : "small"}
              onClick={(e) => {
                stopAll(e);
                openModal("edit-page", node);
              }}
              aria-label="edit-page"
            >
              <EditIcon fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
          </Tooltip>

          <Tooltip title="حذف صفحه">
            <IconButton
              size={isMobile ? "medium" : "small"}
              color="error"
              onClick={(e) => {
                stopAll(e);
                handleDelete(node);
              }}
              aria-label="delete-page"
            >
              <DeleteIcon fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
          </Tooltip>
        </Stack>
      );
    }

    return null;
  };

  const renderLabel = (node) => {
    const isClinic = node.type === "Clinic";
    const isPage = node.type === "Page";

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: isMobile ? 1 : 0.5, // ✅ موبایل تاچ بهتر
          px: 0.5,
          minHeight: isMobile ? 48 : 36,
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
          {isClinic && <ClinicIcon fontSize="small" />}
          {isPage && <PageIcon fontSize="small" />}

          <Typography
            variant={isMobile ? "body1" : "body2"}
            sx={{
              fontWeight: isClinic ? 800 : 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {nodeTitleFa(node)}
          </Typography>

          <Chip
            label={node.type}
            size="small"
            sx={{
              ml: 0.5,
              height: 22,
              fontSize: 11,
              bgcolor: isClinic
                ? "rgba(33,150,243,0.12)"
                : isPage
                ? "rgba(76,175,80,0.12)"
                : "rgba(0,0,0,0.06)",
            }}
          />
        </Box>

        {renderActions(node)}
      </Box>
    );
  };

  const renderTree = (parentId) => {
    const children = childrenMap.get(parentId ?? null) || [];
    return children.map((node) => (
      <TreeItem
        key={node.id}
        itemId={node.id}
        label={renderLabel(node)}
        // ✅ کلیک روی label/row
        onClick={() => {
          if (node.type === "Page") onPageRowClick(node);
        }}
      >
        {renderTree(node.id)}
      </TreeItem>
    ));
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 1.5, bgcolor: "rgba(33,150,243,0.05)" }}>
          <Typography fontWeight={900} sx={{ fontSize: isMobile ? 14 : 13 }}>
            ساختار کلینیک‌ها و صفحات
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: isMobile ? 12 : 11, mt: 0.5 }}>
            روی «صفحه» کلیک کنید تا وارد فرم‌ساز شوید. برای ویرایش/حذف از آیکون‌ها استفاده کنید.
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ p: 1 }}>
          <SimpleTreeView sx={{ direction: "rtl" }}>{renderTree(null)}</SimpleTreeView>
        </Box>
      </Box>

      {/* Clinic dialog */}
      {(mode === "create-clinic" || mode === "edit-clinic") && (
        <Dialog open={openDialog} onClose={closeModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            {mode === "create-clinic" ? "افزودن کلینیک" : "ویرایش کلینیک"}
          </DialogTitle>
          <DialogContent>
            <ClinicForm
              initialData={mode === "edit-clinic" ? selectedNode : null}
              onSubmit={handleClinicSubmit}
              onCancel={closeModal}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Page dialog */}
      {(mode === "create-page" || mode === "edit-page") && (
        <Dialog open={openDialog} onClose={closeModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            {mode === "create-page" ? "افزودن صفحه" : "ویرایش صفحه"}
          </DialogTitle>
          <DialogContent>
            <PageForm
              initialData={mode === "edit-page" ? selectedNode : null}
              onSubmit={handlePageSubmit}
              onCancel={closeModal}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}
